import {
    useState,
    useEffect,
    useContext,
    useCallback,
    useRef,
    memo,
} from "react";
import moment from "moment";
import StarekContext from "../../context/StarekContext";
import Spinner from "../Spinner";
import { getSignalTypeFromBits } from "../../utils";
import DeviceMonitorControl from "./DeviceMonitorControl";
import DeviceGraphView from "./DeviceGraphView";
import DeviceGraphStatus from "./DeviceGraphStatus";

function DeviceGraph({ device, isExpanded = false, sendDeviceCommand }) {
    const [currentSignalIndex, setCurrentSignalIndex] = useState(0);
    const [graphData, setGraphData] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [noData, setNoData] = useState(false);
    const [startTime, setStartTime] = useState(moment().subtract(5, "minute"));
    const [endTime, setEndTime] = useState(moment());
    const [mergedSignalData, setMergedSignalData] = useState({});
    const [signalTypeInfo, setSignalTypeInfo] = useState({});

    // Track the device ID to prevent unnecessary re-fetches when device object reference changes
    const deviceIdRef = useRef(device.id);
    const isExpandedRef = useRef(isExpanded);
    const isMountedRef = useRef(false);

    const starekContext = useContext(StarekContext);
    const { scheme, host, port } = starekContext.lastUsedWorkspace.engine || {};
    const workspace = starekContext.lastUsedWorkspace.name;
    const authToken = starekContext.auth;
    const types = starekContext.types;

    const apiUrl = `${scheme}://${host}:${port}/api/v1/${workspace}/datapoints`;

    // Helper function to send set-signal command
    const sendSetSignalCommand = useCallback(
        async (value) => {
            if (!sendDeviceCommand) {
                console.warn("sendDeviceCommand function not provided");
                return;
            }

            try {
                await sendDeviceCommand({
                    deviceId: device.id,
                    command: "set-signal",
                    parameters: {
                        command: "set-signal",
                        reason: "user-initiated",
                        to: value,
                    },
                });
            } catch (error) {
                console.error("Failed to send set-signal command:", error);
                starekContext.showError?.(
                    `Failed to set signal: ${error.message}`
                );
            }
        },
        [device.id, sendDeviceCommand, starekContext]
    );

    // Dynamic height based on expanded state - make it much larger when expanded
    const containerHeight = isExpanded ? "400px" : "120px";
    const chartMargin = isExpanded
        ? { top: 20, right: 20, left: 20, bottom: 20 }
        : { top: 10, right: 10, left: 10, bottom: 30 };

    const fetchDeviceData = useCallback(
        async (isInitial = false) => {
            if (isInitial) {
                setInitialLoading(true);
            } else {
                setIsRefreshing(true);
            }

            // Get current time in UTC
            const nowUtc = moment().utc();
            const startTimeUtc = nowUtc.clone().subtract(15, "minute");

            // Store as local time for display purposes
            setStartTime(moment.unix(startTimeUtc.unix()));
            setEndTime(moment.unix(nowUtc.unix()));

            const requestBody = {
                devices: [device.id],
                start_time: startTimeUtc.unix(),
                end_time: nowUtc.unix(),
                group_by_device: true,
                group_by_signal: true,
            };

            try {
                const response = await fetch(apiUrl, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(requestBody),
                });

                if (!response.ok)
                    throw new Error(`HTTP error! Status: ${response.status}`);

                const jsonData = await response.json();
                const datapointSignals = jsonData?.data?.[device.id] || {};
                const runtimeSignals = device.device.runtime.signals || {};

                const parameterSignals =
                    device.parameters || device.device?.parameters || {};

                // Normalize runtimeSignals array to object for efficient merging
                const runtimeSignalsMap = {};
                runtimeSignals.forEach((signal) => {
                    if (signal.name) {
                        runtimeSignalsMap[signal.name] = signal;
                    }
                });

                const allKeys = new Set([
                    ...Object.keys(runtimeSignalsMap),
                    ...Object.keys(datapointSignals),
                    ...Object.keys(parameterSignals),
                ]);

                const combinedData = {};

                allKeys.forEach((key) => {
                    const runtime = runtimeSignalsMap[key] || {};
                    const datapoints = datapointSignals?.[key] || [];
                    const parameters = parameterSignals?.[key] || {};

                    const color = parameters.color
                        ? `#${parameters.color}`
                        : "#0080ff";

                    combinedData[key] = {
                        type: runtime.type || null,
                        color: color,
                        datapoints,
                    };
                });

                setNoData(datapointSignals && datapointSignals.length === 0);
                setMergedSignalData(combinedData);
            } catch (error) {
                console.error("Failed to fetch device data:", error);
                setNoData(true);
            } finally {
                if (isInitial) {
                    setInitialLoading(false);
                } else {
                    setIsRefreshing(false);
                }
            }
        },
        [apiUrl, authToken, device.id]
    );

    useEffect(() => {
        // On first mount, always fetch with initial loading
        if (!isMountedRef.current) {
            isMountedRef.current = true;
            deviceIdRef.current = device.id;
            isExpandedRef.current = isExpanded;
            fetchDeviceData(true);
        } else {
            // After mount, check if device ID or isExpanded actually changed
            const deviceIdChanged = deviceIdRef.current !== device.id;
            const isExpandedChanged = isExpandedRef.current !== isExpanded;

            if (deviceIdChanged) {
                // Device changed, do initial load with spinner
                deviceIdRef.current = device.id;
                isExpandedRef.current = isExpanded;
                fetchDeviceData(true);
            } else if (isExpandedChanged) {
                // Only expanded state changed, refresh without initial spinner
                isExpandedRef.current = isExpanded;
                if (isExpanded) {
                    fetchDeviceData(false);
                }
            }
            // If neither changed, this is just a re-render due to device object reference change - do nothing
        }

        // Only set up auto-refresh interval when expanded
        if (isExpanded) {
            const interval = setInterval(() => {
                fetchDeviceData(false);
            }, 30000);
            return () => clearInterval(interval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [device.id, isExpanded]); // fetchDeviceData intentionally omitted to prevent re-running on device object changes

    const signalKeys = Object.keys(mergedSignalData);
    const currentSignalKey = signalKeys[currentSignalIndex] || "";
    const currentSignal = mergedSignalData[currentSignalKey];

    useEffect(() => {
        const updateSignalType = async () => {
            if (currentSignal?.type && types) {
                try {
                    const info = await getSignalTypeFromBits(
                        currentSignal.type,
                        types
                    );
                    setSignalTypeInfo(info);
                } catch (err) {
                    console.error("Failed to decode signal bits:", err);
                    setSignalTypeInfo({});
                }
            } else {
                setSignalTypeInfo({});
            }
        };

        updateSignalType();
    }, [currentSignal?.type, types]);

    useEffect(() => {
        if (!currentSignal) return;
        const formatted = currentSignal.datapoints.map((point) => ({
            time: point.time,
            value: (point.measure_value / 1023) * 100.0,
            measure_value: point.measure_value,
        }));

        if (
            graphData.length !== formatted.length ||
            JSON.stringify(graphData) !== JSON.stringify(formatted)
        ) {
            setGraphData(formatted);
        }
    }, [currentSignal]);

    // Get initial value for control from latest data point (use measure_value for raw value)
    const initialControlValue =
        graphData.length > 0
            ? graphData[graphData.length - 1]?.measure_value || 0
            : 0;

    const prevSignal = () => {
        if (currentSignalIndex > 0) setCurrentSignalIndex((prev) => prev - 1);
    };

    const nextSignal = () => {
        if (currentSignalIndex < signalKeys.length - 1)
            setCurrentSignalIndex((prev) => prev + 1);
    };

    const lineType = (() => {
        if (signalTypeInfo.digital === true && signalTypeInfo.input === true)
            return "stepAfter";
        if (signalTypeInfo.digital === true && signalTypeInfo.output === true)
            return "stepAfter";
        if (signalTypeInfo.analog === true && signalTypeInfo.input === true)
            return "linear";
        if (signalTypeInfo.analog === true && signalTypeInfo.output === true)
            return "linear";
        return "linear";
    })();

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload?.length) {
            const dataPoint = payload[0].payload;
            const rawValue = dataPoint.measure_value || 0;
            return (
                <div className="tw-bg-white tw-p-3 tw-border tw-rounded tw-shadow-lg">
                    <p
                        className={`${
                            isExpanded ? "tw-text-base" : "tw-text-sm"
                        } tw-font-medium`}
                    >
                        {`Value: ${payload[0].value.toFixed(1)}%`}
                    </p>
                    <p
                        className={`${
                            isExpanded ? "tw-text-sm" : "tw-text-xs"
                        } tw-text-gray-600`}
                    >
                        {`Raw: ${rawValue}`}
                    </p>
                    <p
                        className={`${
                            isExpanded ? "tw-text-sm" : "tw-text-xs"
                        } tw-text-gray-600`}
                    >
                        {`Time: ${moment
                            .unix(payload[0].payload.time)
                            .local()
                            .format("HH:mm:ss")}`}
                    </p>
                </div>
            );
        }
        return null;
    };

    const handleValueChange = (value) => {
        sendSetSignalCommand(value);
    };

    if (initialLoading) {
        return (
            <div
                className={`tw-border tw-rounded-lg tw-bg-white tw-w-full tw-flex tw-items-center tw-justify-center ${
                    isExpanded ? "tw-h-full" : ""
                }`}
                style={{ height: isExpanded ? containerHeight : "120px" }}
            >
                <Spinner />
            </div>
        );
    }

    return (
        <div
            className={`tw-border tw-rounded-lg tw-bg-white tw-w-full tw-relative ${
                isExpanded ? "tw-h-full" : ""
            }`}
        >
            {/* Header with navigation and controls */}
            <DeviceMonitorControl
                currentSignalKey={currentSignalKey}
                signalTypeInfo={signalTypeInfo}
                isExpanded={isExpanded}
                currentSignalIndex={currentSignalIndex}
                totalSignals={signalKeys.length}
                onPrevSignal={prevSignal}
                onNextSignal={nextSignal}
                isRefreshing={isRefreshing}
                initialValue={initialControlValue}
                onValueChange={handleValueChange}
            />

            {/* Graph area */}
            <div
                className={`tw-w-full tw-flex tw-items-center tw-justify-center ${
                    isExpanded ? "tw-p-0" : "tw-pr-2"
                }`}
                style={{ height: containerHeight }}
            >
                {noData ? (
                    <div
                        className={`tw-text-gray-500 ${
                            isExpanded ? "tw-text-xl" : "tw-text-sm"
                        }`}
                    >
                        Couldn't find any data
                    </div>
                ) : (
                    <div className="tw-relative tw-w-full tw-h-full">
                        <DeviceGraphView
                            graphData={graphData}
                            signalColor={currentSignal?.color || "#0080ff"}
                            lineType={lineType}
                            isDigital={signalTypeInfo.digital}
                            isExpanded={isExpanded}
                            startTime={startTime}
                            endTime={endTime}
                            chartMargin={chartMargin}
                            CustomTooltip={CustomTooltip}
                        />

                        <DeviceGraphStatus
                            isRefreshing={isRefreshing}
                            isExpanded={isExpanded}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

// Memoize component to prevent re-renders when props haven't changed
export default memo(DeviceGraph, (prevProps, nextProps) => {
    // Only re-render if device.id or isExpanded changes
    return (
        prevProps.device.id === nextProps.device.id &&
        prevProps.isExpanded === nextProps.isExpanded &&
        prevProps.sendDeviceCommand === nextProps.sendDeviceCommand
    );
});
